/**
 * The current app's navigation.
 *
 * Only the pages of the app you are in — Procurement's eleven, not the ERP's
 * hundred — grouped the way the work is. It collapses to an icon rail when a
 * wide table needs the room (remembered per browser), and becomes a drawer on
 * a small screen. On the workspace home there is none at all.
 *
 * The active page is the LONGEST match rather than every prefix: on an
 * external enquiry, "External enquiries" is lit and "RFQs" is not, even
 * though the enquiry lives under /rfqs.
 */
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from '../routes/orgNavigation.jsx';
import { Icon } from '../components/Icon.jsx';
import { AppIcon } from './Brand.jsx';
import { activeItemIn, appDescription, appName, itemLabel, matchStrength, orgRelative, sectionLabel, subItemLabel } from './apps.js';

function NavSkeleton({ rows = 6 }) {
  return (
    <div aria-hidden="true" className="ctxnav-skeleton">
      {Array.from({ length: rows }).map((_row, index) => (
        <div key={index} className="skeleton" style={{ width: `${56 + ((index * 17) % 34)}%` }} />
      ))}
    </div>
  );
}

export function ContextNav({ app, collapsed, onToggleCollapsed, open, onNavigate, pendingApprovals = 0, loading }) {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const name = appName(app, t);
  const plainName = appName(app, t, { bilingual: false });
  const { pathname, search } = orgRelative(location);

  const active = activeItemIn(app.items, pathname, search);

  return (
    <aside
      className={`ctxnav ${collapsed ? 'ctxnav-collapsed' : ''} ${open ? 'ctxnav-open' : ''}`}
      aria-label={t('contextNav.label', { app: plainName })}
      data-app={app.key}
    >
      <Link
        to={app.homeTo ?? app.home}
        className="ctxnav-head"
        onClick={onNavigate}
        title={collapsed ? name : undefined}
        aria-label={collapsed ? t('contextNav.home', { app: plainName }) : undefined}
      >
        <AppIcon app={app} size="md" />
        <span className="ctxnav-head-text">
          <span className="ctxnav-app-name">{name}</span>
          <span className="ctxnav-app-desc">{appDescription(app, t)}</span>
        </span>
      </Link>

      <nav className="ctxnav-nav" aria-busy={loading || undefined}>
        {app.sections.map((section, index) => (
          <div className="ctxnav-section" key={section.label ?? `section-${index}`}>
            {section.label && <div className="ctxnav-label">{sectionLabel(app, section, t)}</div>}
            {section.items.map((item) => {
              const isActive = item === active;
              const showCount = item.badge === 'approvals' && pendingApprovals > 0;
              const label = itemLabel(item, t);
              const awaiting = t('contextNav.awaitingApproval', { count: pendingApprovals });
              return (
                <div key={item.to} className="ctxnav-item">
                  <Link
                    to={item.to}
                    className={`ctxnav-link ${isActive ? 'ctxnav-link-active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={onNavigate}
                    title={collapsed ? label : undefined}
                    // Collapsed, the label is hidden — from screen readers too —
                    // so the link carries it as its name.
                    aria-label={
                      collapsed
                        ? showCount
                          ? t('contextNav.withCount', { label: itemLabel(item, t, { bilingual: false }), awaiting: t('contextNav.awaitingApproval', { count: pendingApprovals, bilingual: false }) })
                          : label
                        : undefined
                    }
                  >
                    <Icon name={item.icon} size={16} className="ctxnav-link-icon" />
                    <span className="ctxnav-link-label">{label}</span>
                    {showCount && (
                      <span className="ctxnav-count" title={awaiting}>
                        {pendingApprovals}
                      </span>
                    )}
                  </Link>
                  {item.children && isActive && !collapsed && (
                    <div className="ctxnav-sub">
                      {item.children.map((child) => {
                        const childActive = matchStrength({ ...child, end: true }, pathname, search) > 0;
                        return (
                          <Link
                            key={child.to}
                            to={child.to}
                            className={`ctxnav-sublink ${childActive ? 'ctxnav-sublink-active' : ''}`}
                            aria-current={childActive ? 'page' : undefined}
                            onClick={onNavigate}
                          >
                            {subItemLabel(child, t)}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {loading && <NavSkeleton />}
      </nav>

      <div className="ctxnav-foot">
        <button
          type="button"
          className="ctxnav-collapse"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? t('contextNav.expand') : t('contextNav.collapse')}
          title={collapsed ? t('contextNav.expand') : t('contextNav.collapse')}
        >
          <Icon name={collapsed ? 'panel-left-open' : 'panel-left-close'} size={16} />
          <span className="ctxnav-link-label">{t('contextNav.collapseShort')}</span>
        </button>
      </div>
    </aside>
  );
}
